
import { Injectable } from '@angular/core';
import { filter, first, map, switchMap, take, tap, timeout, catchError } from 'rxjs/operators';
import { ServerResponse } from '@app/enums/server-response';
import { Store, select } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { LobbyService } from '../../../features/lobby/services/lobby.service';

import * as LobbyActions from '../lobby/lobby.actions';
import * as TableSummariesActions from './table-summaries.actions';
import * as TableSummariesSelectors from './table-summaries.selectors';

import * as ConfigSelectors from '@app/store/features/config/config.selectors';

import { WsService } from '@app/services/ws.service';
import { TableSummary, TableSummaryData } from '@app/models/table-summary';
import { LiveLobbyRing } from '@app/models/live-lobby-ring';
import { LiveLobbyRingSelected } from '@app/models/live-lobby-ring-selected';
import * as UserSelectors from '@app/store/features/user/user.selectors';
import { combineLatest, forkJoin, of, EMPTY } from 'rxjs';
import * as CurrenciesSelectors from '@app/store/features/currencies/currencies.selectors';

@Injectable()
export class TableSummariesEffects {
    constructor(
        private _ws: WsService,
        private readonly store: Store,
        private readonly lobbyService: LobbyService,
        private actions$: Actions,

    ) { }

    // updateActiveNav$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(LobbyActions.updateActiveNav),
    //             filter(({ activeNav }) => activeNav === LobbyNav.RingGames),
    //             tap(() => this.lobbyService.getRingTables()),
    //             map(() => TableSummariesActions.updateLoader({ loader: true }))
    //         ));


    getTableSummary$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(TableSummariesActions.getTableSummary),
                tap(({ tableId }) => {
                    this.lobbyService.getTableSummary(tableId);
                }),
            ), { dispatch: false }); // Return Table Summaries Array

    updateTableSummaries$ = createEffect(() =>
        this._ws.getDataResponse<TableSummaryData[]>(ServerResponse.TableSummaries)
            .pipe(
                // 🔧 FIX: Use switchMap to cancel previous operations
                switchMap(tableSummariesResponse =>
                    combineLatest([
                        of(tableSummariesResponse),
                        this.store.pipe(select(CurrenciesSelectors.selectAll), take(1)),
                        this.store.pipe(select(ConfigSelectors.getJackPotsInfo), take(1))
                    ])
                ),
                map(([tableSummariesResponse, currencies, jackPotsInfo]) => {
                    const tableSummaries: TableSummary[] = tableSummariesResponse.map(tableSummaryData => {
                        const currencyInfo = currencies.find(currency => currency.id === tableSummaryData.currency)
                        return this.lobbyService.updateTableSummary(tableSummaryData, jackPotsInfo, currencyInfo!);
                    })

                    return TableSummariesActions.setAll({ tableSummaries })
                }),
                catchError((error) => {
                    console.warn('updateTableSummaries$ error:', error);
                    return EMPTY;
                })
            ));

    // Private Tables
    tableSummary$ = createEffect(() => this._ws.getDataResponse<TableSummaryData>(ServerResponse.TableSummary)
        .pipe(
            // 🔧 FIX: Use switchMap + combineLatest with take(1) instead of forkJoin
            switchMap(tableSummaryData =>
                combineLatest([
                    of(tableSummaryData),
                    this.store.pipe(select(CurrenciesSelectors.selectAll), take(1)),
                    this.store.pipe(select(ConfigSelectors.getJackPotsInfo), take(1))
                ])
            ),
            map(([tableSummaryData, currencies, jackPotsInfo]) => {
                const currencyInfo = currencies.find(currency => currency.id === tableSummaryData.currency)
                const tableSummary = this.lobbyService.updateTableSummary(tableSummaryData, jackPotsInfo, currencyInfo!);

                return TableSummariesActions.upsertOne({ tableSummary: tableSummary })
            }),
            catchError((error) => {
                console.warn('tableSummary$ error:', error);
                return EMPTY;
            })
        )
    )


    updateLiveLobbyRingSelected$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyRingSelected>(ServerResponse.LiveLobbyRingSelected)
            .pipe(
                // 🔧 FIX: Flatten nested subscriptions
                switchMap(liveLobbyRingSelected =>
                    combineLatest([
                        of(liveLobbyRingSelected),
                        this.getUserProfile(),
                        this.store.pipe(
                            select(TableSummariesSelectors.selectEntityById(liveLobbyRingSelected.idTable)),
                            filter(tableSummary => !!tableSummary),
                            take(1),
                            timeout(5000),
                            catchError(() => EMPTY)
                        ),
                        this.store.pipe(select(CurrenciesSelectors.selectAll), take(1)),
                        this.store.pipe(select(ConfigSelectors.getJackPotsInfo), take(1))
                    ])
                ),
                map(([liveLobbyRingSelected, userProfile, tableSummary, currencies, jackPotsInfo]) => {
                    const currencyInfo = currencies.find(currency => currency.id === tableSummary!.currency)

                    // Immutable update - no mutation
                    const updateTableSummary: TableSummary = {
                        ...tableSummary as TableSummary,
                        imSitOnTheTable: liveLobbyRingSelected.players.findIndex(player => player.playerId === userProfile.id) > -1
                    }
                    const tableSummaryUpdate = this.lobbyService.updateTableSummary(updateTableSummary, jackPotsInfo, currencyInfo!)
                    return TableSummariesActions.updateOne({ tableId: liveLobbyRingSelected.idTable, tableSummary: tableSummaryUpdate })
                }),
                catchError((error) => {
                    console.warn('updateLiveLobbyRingSelected$ error:', error);
                    return EMPTY;
                })
            )
    );


    updateLiveLobbyRing$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyRing>(ServerResponse.LiveLobbyRing)
            .pipe(
                // 🔧 FIX: Use switchMap + combineLatest instead of forkJoin
                switchMap(liveLobbyRing =>
                    combineLatest([
                        of(liveLobbyRing),
                        this.store.pipe(select(CurrenciesSelectors.selectAll), take(1)),
                        this.store.pipe(select(ConfigSelectors.getJackPotsInfo), take(1)),
                        // 🔧 FIX: Get table summary if not new table
                        liveLobbyRing.isNewTable
                            ? of(null)
                            : this.store.pipe(
                                  select(TableSummariesSelectors.selectEntityById(liveLobbyRing.idTable)),
                                  take(1)
                              )
                    ])
                ),
                map(([liveLobbyRing, currencies, jackPotsInfo, tableSummary]) => {
                    if (liveLobbyRing.isNewTable) {
                        const tableSummaryData = liveLobbyRing.summary!;
                        const currencyInfo = currencies.find(currency => currency.id === tableSummaryData!.currency)
                        const newTableSummary: TableSummary = this.lobbyService.updateTableSummary(tableSummaryData, jackPotsInfo, currencyInfo!)
                        return TableSummariesActions.addOne({ tableSummary: newTableSummary })
                    }

                    if (liveLobbyRing.removed) {
                        return TableSummariesActions.removeOne({ tableId: liveLobbyRing.idTable })
                    }

                    const currencyInfo = currencies.find(currency => currency.id === tableSummary!.currency)

                    // Immutable update - no mutation
                    const updateTableSummary: TableSummary = {
                        ...tableSummary as TableSummary,
                        ...liveLobbyRing.summary,
                        playerCount: liveLobbyRing.nbPlayers,
                        averagePot: liveLobbyRing.averagePot,
                        playerFlop: liveLobbyRing.playerFlop,
                        handPerHour: liveLobbyRing.handPerHour,
                        waitingList: liveLobbyRing.waitingList,
                    }
                    const tableSummaryUpdate = this.lobbyService.updateTableSummary(updateTableSummary, jackPotsInfo, currencyInfo!)

                    return TableSummariesActions.updateOne({ tableId: liveLobbyRing.idTable, tableSummary: tableSummaryUpdate })
                }),
                catchError((error) => {
                    console.warn('updateLiveLobbyRing$ error:', error);
                    return EMPTY;
                })
            )
    );

    private getUserProfile() {
        return this.store.pipe(
            select(UserSelectors.selectUserProfile),
            filter(el => !!el),
            map(el => el!),
            take(1)
        )
    }
} 